import type { SerializedError } from '@reduxjs/toolkit';
import clsx from 'clsx';
import type { ComponentProps, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TAxiosBaseResponseError, TAxiosInterceptorResponseError } from 'rtk/query/rtk-query.types';
import { isTAxiosInterceptorResponseError, isTAxiosResponseError } from 'rtk/query/rtk-query.types';
import MutedText from '../MutedText';
import './styles.css';

// Type guard function
export function isTSerializedError(obj: unknown): obj is SerializedError {
  if (!obj || typeof obj !== 'object') {
    return false;
  }
  return (
    typeof (obj as SerializedError).name === 'string' ||
    typeof (obj as SerializedError).message === 'string' ||
    typeof (obj as SerializedError).stack === 'string' ||
    typeof (obj as SerializedError).code === 'string'
  );
}

interface Props extends ComponentProps<'div'> {
  error:
    | TAxiosInterceptorResponseError
    | SerializedError
    | TAxiosBaseResponseError
    | {
        status: string | number;
        statusText: string;
        message: string;
      };
  children?: ReactNode;
}

const IsAxiosError = ({ error, children, ...props }: Props) => {
  console.log('<IsAxiosError />', error);
  const { t } = useTranslation();

  if (isTAxiosInterceptorResponseError(error)) {
    const message = error.status === 401 ? t('errors.unauthorized401') : error.message;
    return (
      <div {...props} className={clsx('flex flex-col justify-between gap-4', 'bg-danger-50', 'p-4', 'rounded-md')}>
        <div className={clsx('flex flex-row justify-start')}>
          <MutedText className={clsx('text-danger-300 text-sm')}>
            {error.status}: {error.statusText}
          </MutedText>
        </div>

        {message && message.length ? (
          <div {...props} className={clsx('text-danger-800')}>
            {message}
          </div>
        ) : null}
        {children}
      </div>
    );
  }

  if (isTAxiosResponseError(error)) {
    return <div>isTAxiosResponseError</div>;
  }

  if (isTSerializedError(error)) {
    return <div>isTSerializedError</div>;
  }

  const message =
    'status' in error && error.status === 401
      ? t('errors.unauthorized401')
      : 'message' in error
        ? error.message
        : undefined;
  return (
    <div {...props} className={clsx('flex flex-col justify-between gap-4', 'bg-danger-50', 'p-4', 'rounded-md')}>
      <div className={clsx('flex flex-row justify-start')}>
        {'status' in error && (
          <MutedText className={clsx('text-danger-300 text-sm')}>
            {error.status}: {error.statusText}
          </MutedText>
        )}
      </div>

      {message && message.length ? (
        <div {...props} className={clsx('text-danger-800')}>
          {message}
        </div>
      ) : null}

      {children}
    </div>
  );
};

export default IsAxiosError;
